/* ==============================================================================
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
============================================================================== */

import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

// @mui material components
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDBadgeDot from 'components/MDBadgeDot';

// Material Dashboard 2 PRO React examples
import PieChart from 'examples/Charts/PieChart';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import Footer from 'examples/Footer';
import DataTable from 'examples/Tables/DataTable';
import ComplexStatisticsCard from 'examples/Cards/StatisticsCards/ComplexStatisticsCard';
import DefaultLineChart from 'examples/Charts/LineCharts/DefaultLineChart';
import VerticalBarChart from 'examples/Charts/BarCharts/VerticalBarChart';
import HorizontalBarChart from 'examples/Charts/BarCharts/HorizontalBarChart';
import ProductCell from 'layouts/dashboards/sales/components/ProductCell';
import RefundsCell from 'layouts/dashboards/sales/components/RefundsCell';

import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import * as Func from 'services/Function';

// Material Dashboard 2 PRO React examples
import Navbar from 'examples/Navbars/NavBar';

const initChart = {
  title: '',
  description: '',
  date: '',
  chart: {
    labels: [],
    datasets: [],
  },
};

function DashboardSale() {
  const mod = 'dashboard_sale';
  const { t, i18n } = useTranslation();
  const app = useSelector((state) => state[APP_STATE]);
  const [sale, setSale] = useState([]);
  const [invoice, setInvoice] = useState([]);
  const [chartPie, setChartPie] = useState(initChart);
  const [chartLine, setChartLine] = useState(initChart);
  const [chartVertical, setChartVertical] = useState(initChart);
  const [chartHorizontal, setChartHorizontal] = useState(initChart);
  const [stat, setStat] = useState({
    stat1: { count: '', color: 'white', amount: '', label: '' },
    stat2: { count: '', color: 'white', amount: '', label: '' },
    stat3: { count: '', color: 'white', amount: '', label: '' },
    stat4: { count: '', color: 'white', amount: '', label: '' },
    stat5: { count: '', color: 'white', amount: '', label: '' },
    stat6: { count: '', color: 'white', amount: '', label: '' },
  });

  const init = () => {
    FetchData('GET', `/${mod}`, app.token, {
      lang: app.lang,
    }).then((result) => {
      if (result.status === 'ok') {
        setStat(result.stat);
        setChartPie(result.pie);
        setChartLine(result.line);
        setChartVertical(result.vertical);
        setChartHorizontal(result.horizontal);
        setSale(result.sale);
        setInvoice(result.invoice);
      } else {
        // Func.exitApp();
      }
    });
  };

  const renderStat = () => {
    return (
      <MDBox>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6} lg={2}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color='dark'
                icon='hourglass_top'
                title='ยอดขาย'
                count={stat.stat1.count}
                percentage={{
                  color: stat.stat1.color,
                  amount: stat.stat1.amount,
                  label: stat.stat1.label,
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={2}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color='primary'
                icon='real_estate_agent'
                title='ดรอปเงิน'
                count={stat.stat2.count}
                percentage={{
                  color: stat.stat2.color,
                  amount: stat.stat2.amount,
                  label: stat.stat2.label,
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={2}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color='warning'
                icon='shopping_bag'
                title='เปลี่ยนของ'
                count={stat.stat3.count}
                percentage={{
                  color: stat.stat3.color,
                  amount: stat.stat3.amount,
                  label: stat.stat3.label,
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={2}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color='error'
                icon='cancel'
                title='ยกเลิก'
                count={stat.stat4.count}
                percentage={{
                  color: stat.stat4.color,
                  amount: stat.stat4.amount,
                  label: stat.stat4.label,
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={2}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color='info'
                icon='add_business'
                title='ปิดรอบ'
                count={stat.stat5.count}
                percentage={{
                  color: stat.stat5.color,
                  amount: stat.stat5.amount,
                  label: stat.stat5.label,
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={2}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color='success'
                icon='check_circle'
                title='อนุมัติ'
                count={stat.stat6.count}
                percentage={{
                  color: stat.stat6.color,
                  amount: stat.stat6.amount,
                  label: stat.stat6.label,
                }}
              />
            </MDBox>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderChart = () => {
    return (
      <MDBox mb={3} mt={1}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <DefaultLineChart
              title='ยอดขายหน้าร้านแยกตามสาขา'
              description={
                <MDBox display='flex' justifyContent='space-between'>
                  <MDBox display='flex' ml={-1}>
                    {chartLine.chart.datasets.map((row) => (
                      <MDBadgeDot
                        color={row.color}
                        size='sm'
                        badgeContent={row.label}
                        key={row.label}
                      />
                    ))}
                  </MDBox>
                </MDBox>
              }
              chart={chartLine.chart}
            />
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderBar = () => {
    return (
      <MDBox mb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12} lg={6}>
            <HorizontalBarChart
              title='จำนวนการเปิดขายหน้าร้าน'
              description='จัดลำดับย้อนหลัง 7 วัน ล่าสุด'
              chart={chartHorizontal.chart}
            />
          </Grid>
          <Grid item xs={12} lg={6}>
            <VerticalBarChart
              title='ยอดการขายออนไลน์'
              description='ยอดขายต่อวัน'
              chart={chartVertical.chart}
            />
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderTable = () => {
    return (
      <Grid container spacing={3}>
        <Grid item xs={12} lg={6}>
          <Card>
            <MDBox pt={3} px={3}>
              <MDTypography variant='h6' fontWeight='medium'>
                ปริมาณการขายหน้าร้านสูงสุดย้อนหลัง 1 สัปดาห์
              </MDTypography>
            </MDBox>
            <MDBox py={1}>
              <DataTable
                table={{
                  columns: [
                    { Header: 'สินค้า', accessor: 'product', width: '55%' },
                    { Header: 'ปริมาณ', accessor: 'quantity', align: 'right' },
                    { Header: 'หน่วย', accessor: 'unit' },
                    { Header: '', accessor: 'refunds', align: 'right' },
                  ],

                  rows: sale.map((item) => {
                    return {
                      product: (
                        <ProductCell image={item.image} name={item.code} orders={item.name} />
                      ),
                      quantity: Func.numberDisplay(item.quantity),
                      unit: item.unit_name,
                      refunds:
                        item.compare === 'Y' ? (
                          <RefundsCell
                            value={Func.numberDisplay(item.diff)}
                            icon={{ color: 'success', name: 'keyboard_arrow_up' }}
                          />
                        ) : (
                          <RefundsCell
                            value={Func.numberDisplay(item.diff)}
                            icon={{ color: 'error', name: 'keyboard_arrow_down' }}
                          />
                        ),
                    };
                  }),
                }}
                entriesPerPage={false}
                showTotalEntries={false}
                isSorted={false}
                noEndBorder
              />
            </MDBox>
          </Card>
        </Grid>
        <Grid item xs={12} lg={6}>
          <Card>
            <MDBox pt={3} px={3}>
              <MDTypography variant='h6' fontWeight='medium'>
                ปริมาณการขายออนไลน์สูงสุดย้อนหลัง 1 สัปดาห์
              </MDTypography>
            </MDBox>
            <MDBox py={1}>
              <DataTable
                table={{
                  columns: [
                    { Header: 'สินค้า', accessor: 'product', width: '55%' },
                    { Header: 'ปริมาณ', accessor: 'quantity', align: 'right' },
                    { Header: 'หน่วย', accessor: 'unit' },
                    { Header: '', accessor: 'refunds', align: 'right' },
                  ],

                  rows: invoice.map((item) => {
                    return {
                      product: (
                        <ProductCell image={item.image} name={item.code} orders={item.name} />
                      ),
                      quantity: Func.numberDisplay(item.quantity),
                      unit: item.unit_name,
                      refunds:
                        item.compare === 'Y' ? (
                          <RefundsCell
                            value={Func.numberDisplay(item.diff)}
                            icon={{ color: 'success', name: 'keyboard_arrow_up' }}
                          />
                        ) : (
                          <RefundsCell
                            value={Func.numberDisplay(item.diff)}
                            icon={{ color: 'error', name: 'keyboard_arrow_down' }}
                          />
                        ),
                    };
                  }),
                }}
                entriesPerPage={false}
                showTotalEntries={false}
                isSorted={false}
                noEndBorder
              />
            </MDBox>
          </Card>
        </Grid>
      </Grid>
    );
  };

  useEffect(() => {
    i18n.changeLanguage(app.lang);

    init();
  }, []);

  return (
    <DashboardLayout>
      <Navbar route={[t(`menu.dashboard`), t(`menu.dashboard_sale`)]} />
      <MDBox py={3}>
        {renderStat()}
        {renderChart()}
        {renderBar()}
        {renderTable()}
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default DashboardSale;
